// =============================================================================
// Prospect Data Room Detail Page (Phase II)
// =============================================================================

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Card, Button } from '../../components/ui';
import { fundraisingService } from '../../services/fundraising.service';
import { getApiError } from '../../services/api';
import type { ProspectDataRoomDocument } from '../../types/fundraising';

// =============================================================================
// Document Type Icons/Labels
// =============================================================================

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  PPM: 'Private Placement Memorandum',
  SUBSCRIPTION_AGREEMENT: 'Subscription Agreement',
  OPERATING_AGREEMENT: 'Operating Agreement',
  FINANCIAL_STATEMENT: 'Financial Statement',
  QUARTERLY_REPORT: 'Quarterly Report',
  K1: 'K-1 Tax Document',
  OTHER: 'Other Document',
};

// =============================================================================
// Component
// =============================================================================

export function DataRoomDetailPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [documents, setDocuments] = useState<ProspectDataRoomDocument[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const loadDocuments = useCallback(async () => {
    if (!dealId) return;

    setIsLoading(true);
    setError(null);
    try {
      const data = await fundraisingService.getProspectDataRoomDocuments(dealId);
      setDocuments(data);
    } catch (err) {
      console.error('Failed to load documents:', err);
      const apiError = getApiError(err);
      // Check if NDA required
      if (apiError.message?.includes('NDA')) {
        navigate(`/prospect/datarooms/${dealId}/nda`);
        return;
      }
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [dealId, navigate]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleDownload = async (doc: ProspectDataRoomDocument) => {
    if (!dealId) return;

    setDownloadingId(doc.id);
    try {
      const result = await fundraisingService.downloadProspectDocument(dealId, doc.id);
      // In production, this would redirect to a signed URL or trigger download
      // For now, we'll just show the path
      window.open(result.filePath, '_blank');
    } catch (err) {
      console.error('Failed to download document:', err);
      setError(getApiError(err).message);
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  const getDocumentTypeLabel = (type: string) => {
    return DOCUMENT_TYPE_LABELS[type] || type;
  };

  // Group documents by type
  const groupedDocuments = documents.reduce((acc, doc) => {
    const type = doc.type;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(doc);
    return acc;
  }, {} as Record<string, ProspectDataRoomDocument[]>);

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-96 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/prospect/datarooms')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Data Room Documents</h1>
          <p className="text-sm text-gray-500 mt-1">
            Review available investment materials
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Documents */}
      {documents.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No documents available</h3>
            <p className="mt-2 text-sm text-gray-500">
              There are no documents in this data room yet.
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedDocuments).map(([type, docs]) => (
            <Card key={type} padding="none">
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
                <h3 className="font-medium text-gray-900">{getDocumentTypeLabel(type)}</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {docs.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <DocumentTextIcon className="h-6 w-6 text-gray-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">{doc.name}</h4>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Uploaded {formatDate(doc.uploadedAt)}
                          {doc.uploadedBy?.name && ` by ${doc.uploadedBy.name}`}
                        </p>
                        {/* View Status */}
                        <div className="flex items-center gap-3 mt-2">
                          {doc.viewedAt && (
                            <span className="inline-flex items-center gap-1 text-xs text-green-600">
                              <EyeIcon className="h-3.5 w-3.5" />
                              Viewed {formatDate(doc.viewedAt)}
                            </span>
                          )}
                          {doc.downloadedAt && (
                            <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                              <CheckCircleIcon className="h-3.5 w-3.5" />
                              Downloaded {formatDate(doc.downloadedAt)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleDownload(doc)}
                      disabled={downloadingId === doc.id}
                    >
                      {downloadingId === doc.id ? (
                        'Downloading...'
                      ) : (
                        <>
                          <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                          Download
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default DataRoomDetailPage;
